/**
 * Calculate care gap status for a single group based on population counts.
 * Logic:
 * - If initial-population count < 1 → ineligible (patient not eligible from the start)
 * - If patient is in denominator-exclusion → excluded (separate from ineligible)
 * - If denominator count < 1 → ineligible (patient in initial-pop but doesn't meet denominator criteria)
 * - If patient is in numerator (and denominator) → completed
 * - If patient is in denominator but NOT numerator → open
 */
export function calculateGroupStatus(group: {
  population?: Array<{
    code?: { coding?: Array<{ code?: string }> };
    count?: number;
  }>;
}): "completed" | "open" | "ineligible" | "excluded" {
  const populations = group.population || [];

  // Find population counts
  let initialPopulation = 0;
  let denominatorExclusion = 0;
  let denominator = 0;
  let numerator = 0;

  for (const pop of populations) {
    const popCode = pop.code?.coding?.[0]?.code?.toLowerCase() || "";
    const count = pop.count || 0;

    if (popCode === "initial-population") {
      initialPopulation = count;
    } else if (popCode === "denominator-exclusion") {
      denominatorExclusion = count;
    } else if (popCode === "denominator") {
      denominator = count;
    } else if (popCode === "numerator") {
      numerator = count;
    }
  }

  // Apply logic - check initial population first
  if (initialPopulation < 1) {
    return "ineligible";
  }

  // Check denominator-exclusion separately (excluded status)
  if (denominatorExclusion > 0) {
    return "excluded";
  }

  // Check if patient is in the denominator (eligible for the measure)
  if (denominator < 1) {
    return "ineligible"; // In initial-pop but not in denominator = not eligible
  }

  // After checking denominator membership, check if numerator < 1
  if (numerator < 1) {
    return "open"; // In denominator but not numerator = gap exists
  }

  // Otherwise, if numerator >= 1, the care gap is completed
  return "completed";
}

/**
 * Calculate aggregate care gap status across all groups.
 * When multiple groups exist with different statuses, uses priority order:
 * 1. open (highest priority - indicates a gap that needs attention)
 * 2. completed
 * 3. excluded
 * 4. ineligible (lowest priority)
 */
export function calculateCareGapStatus(measureReport: {
  group?: Array<{
    population?: Array<{
      code?: { coding?: Array<{ code?: string }> };
      count?: number;
    }>;
  }>;
}): "completed" | "open" | "ineligible" | "excluded" {
  const groups = measureReport.group;
  if (!groups || groups.length === 0) {
    return "open";
  }

  // Calculate status for each group
  const groupStatuses = groups.map(group => calculateGroupStatus(group));

  // Priority order: open > completed > excluded > ineligible
  if (groupStatuses.some(status => status === "open")) {
    return "open";
  }
  if (groupStatuses.some(status => status === "completed")) {
    return "completed";
  }
  if (groupStatuses.some(status => status === "excluded")) {
    return "excluded";
  }

  return "ineligible";
}

/**
 * Get color scheme for status badge
 */
export function getStatusColor(status: string): string {
  switch (status.toLowerCase()) {
    case "completed":
      return "green";
    case "open":
      return "red";
    case "ineligible":
      return "gray";
    case "excluded":
      return "purple";
    case "complete":
      return "green";
    case "pending":
      return "yellow";
    case "error":
      return "red";
    default:
      return "gray";
  }
}

/**
 * Format status for display
 * Capitalizes the first letter (e.g., "completed" → "Completed")
 */
export function formatStatus(status: string): string {
  return status.charAt(0).toUpperCase() + status.slice(1);
}

/**
 * Format population code for display
 * Converts "initial-population" to "Initial Population"
 */
export function formatPopulationCode(code: string): string {
  return code
    .split("-")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Format PascalCase string to readable format
 * Converts "BreastCancerScreening" to "Breast Cancer Screening"
 */
export function formatPascalCase(text: string): string {
  return text.replace(/([A-Z])/g, " $1").trim();
}
